/**
 * Sentry Error Monitoring — Dual DSN (Dev + Đối tác)
 * 
 * Import file này ĐẦU TIÊN trong main.tsx
 * 
 * - DSN #1 (Dev): Team Dev quản lý → dùng Sentry.init() chính
 * - DSN #2 (Partner): Đối tác quản lý → forward qua beforeSend
 */
import * as Sentry from "@sentry/react";

// ====== Config ======
const SENTRY_DSN_DEV: string = "https://aff8b47c641d14c08f326884223e79cd@o4511185021435905.ingest.us.sentry.io/4511185023926272";     // ---- Thay bằng DSN từ sentry.io
const SENTRY_DSN_PARTNER: string = ""; // ---- Đối tác gửi DSN sau

const IS_PRODUCTION = import.meta.env.MODE === "production";

// ====== Sentry #1: Dev Team (SDK chính) ======
// Cho phép chạy cả development để test — lỗi sẽ tag environment="development" trên dashboard
if (SENTRY_DSN_DEV) {
  Sentry.init({
    dsn: SENTRY_DSN_DEV,
    environment: import.meta.env.MODE,

    // Performance: 20% requests
    tracesSampleRate: 0.2,

    // Session Replay: ghi lại thao tác khi có lỗi
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      new Sentry.BrowserTracing(),
      new Sentry.Replay(),
    ],

    // Lọc bỏ noise từ rác môi trường Browser / Extensions
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      /Loading chunk .* failed/,
      "Network Error",
      "AbortError",
      // Ignore Zalo in-app Browser injection
      "zaloJSV2",
      "Can't find variable: zaloJSV2",
      // Ignore iOS WebView injection
      "webkit.messageHandlers",
      // Ignore Google Translate DOM mutations
      "Failed to execute 'insertBefore' on 'Node'",
      "NotFoundError: Failed to execute 'insertBefore' on 'Node'",
      // Ignore generic object throws
      "Object captured as exception with keys: message",
    ],

    // Hook: khi Dev Sentry bắt event, gửi copy sang Partner
    beforeSend(event) {
      if (SENTRY_DSN_PARTNER) {
        sendToPartner(event as unknown as Record<string, unknown>);
      }
      return event;
    },
  });
}

/**
 * Gửi bản copy lỗi sang Sentry Đối tác qua Store API
 * Fire-and-forget — không ảnh hưởng performance
 */
function sendToPartner(event: Record<string, unknown>) {
  if (!SENTRY_DSN_PARTNER) return;

  try {
    const dsnMatch = SENTRY_DSN_PARTNER.match(
      /^https?:\/\/(.+)@(.+)\/(.+)$/
    );
    if (!dsnMatch) return;

    const [, publicKey, host, projectId] = dsnMatch;
    const endpoint = `https://${host}/api/${projectId}/store/?sentry_key=${publicKey}&sentry_version=7`;

    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...event,
        platform: "javascript",
        sdk: { name: "sentry.javascript.react", version: "7.120.4" },
      }),
    }).catch(() => {
      // Silent fail — không ảnh hưởng user
    });
  } catch {
    // Silent fail
  }
}

// Gán vào window để test từ Console trình duyệt (F12)
// Gõ: Sentry.captureMessage("test từ local")
if (!IS_PRODUCTION) {
  (window as any).Sentry = Sentry;
}

export { Sentry };
